import { ActionIcon, Button, Group, Modal, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { IconCheck, IconPlus, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { ConfirmActionModal } from "@how/ui";
import {
  useDeleteCategory,
  useGroupCategories,
  useSaveCategory,
  type GroupCategory,
} from "./group-queries";

export interface CategoriesModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
}

/** The church's group categories: a short, ordered list. Deleting one leaves its groups with no category. */
export function CategoriesModal({ opened, onClose }: CategoriesModalProps) {
  const categories = useGroupCategories();
  const save = useSaveCategory();
  const remove = useDeleteCategory();
  const [drafts, setDrafts] = useState<Record<string, string | undefined>>({});
  const [adding, setAdding] = useState("");
  const [deleting, setDeleting] = useState<GroupCategory | null>(null);
  const list = categories.data ?? [];
  const failed = save.isError || remove.isError;

  return (
    <>
      <Modal opened={opened} onClose={onClose} title="Group categories" radius="lg">
        <Stack gap="sm">
          {list.length === 0 ? (
            <Text size="sm" c="dimmed">
              No categories yet. They group the finder: Adults, Youth, Families.
            </Text>
          ) : (
            list.map((category) => {
              const draft = drafts[category.id];
              return (
                <Group key={category.id} gap="xs" wrap="nowrap">
                  <TextInput
                    aria-label={`Rename ${category.name}`}
                    value={draft ?? category.name}
                    style={{ flex: 1 }}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setDrafts((current) => ({ ...current, [category.id]: value }));
                    }}
                  />
                  <Tooltip label="Save the new name">
                    <ActionIcon
                      variant="subtle"
                      aria-label={`Save ${category.name}`}
                      disabled={
                        draft === undefined ||
                        draft.trim() === category.name ||
                        draft.trim().length === 0
                      }
                      loading={save.isPending && save.variables.categoryId === category.id}
                      onClick={() => {
                        save.mutate(
                          {
                            categoryId: category.id,
                            name: (draft ?? "").trim(),
                            sortOrder: category.sortOrder,
                          },
                          {
                            onSuccess: () => {
                              setDrafts((current) => ({ ...current, [category.id]: undefined }));
                            },
                          },
                        );
                      }}
                    >
                      <IconCheck size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={`Delete ${category.name}`}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={`Delete ${category.name}`}
                      onClick={() => {
                        setDeleting(category);
                      }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              );
            })
          )}
          <Group gap="xs" wrap="nowrap" align="flex-end">
            <TextInput
              label="New category"
              placeholder="Young adults"
              value={adding}
              style={{ flex: 1 }}
              onChange={(event) => {
                setAdding(event.currentTarget.value);
              }}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              disabled={adding.trim().length === 0}
              loading={save.isPending && save.variables.categoryId === null}
              onClick={() => {
                save.mutate(
                  { categoryId: null, name: adding.trim(), sortOrder: list.length },
                  {
                    onSuccess: () => {
                      setAdding("");
                    },
                  },
                );
              }}
            >
              Add
            </Button>
          </Group>
          {failed ? (
            <Text size="sm" c="red">
              {(save.error as { code?: string } | null)?.code === "23505"
                ? "A category with that name already exists."
                : "That change was not saved. Check your access and try again."}
            </Text>
          ) : null}
        </Stack>
      </Modal>
      <ConfirmActionModal
        opened={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        title="Delete category"
        message={
          deleting === null
            ? ""
            : `Delete ${deleting.name}? Its groups keep going with no category.`
        }
        confirmLabel="Delete"
        danger
        loading={remove.isPending}
        onConfirm={() => {
          if (deleting === null) return;
          remove.mutate(deleting.id, {
            onSuccess: () => {
              setDeleting(null);
            },
          });
        }}
      />
    </>
  );
}
